import { t } from "@lingui/macro";

// Date adapter: libs/schema stores a single freeform `date` string per entry
// (e.g. "Jan 2020 - Present", matching how resumes are actually written and
// how AI extraction produces it), while the builder's design form uses
// separate startDate/endDate/current fields bound to <input type="date">,
// which requires strict ISO (yyyy-mm-dd) values. Native `Date` parsing
// accepts "Month Year" and "Mon Year" formats directly; unparseable text
// degrades to an empty picker rather than a garbled one.

export function toIsoDate(text: string): string {
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{4}$/.test(text)) return `${text}-01-01`;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export const parseDateString = (
  date: string,
): { startDate: string; endDate: string; current: boolean } => {
  if (!date) return { startDate: "", endDate: "", current: false };
  // AI-produced dates can use an en dash (–) or em dash (—) instead of a
  // plain hyphen (observed live from real Groq output: "Sep 2019 – Dec 2021").
  const parts = date.split(/\s[–—-]\s/);
  const start = parts[0]?.trim() ?? "";
  const endRaw = parts[1]?.trim() ?? "";
  const current = endRaw.toLowerCase() === "present" || endRaw.toLowerCase() === "current";
  return {
    startDate: toIsoDate(start),
    endDate: current ? "" : toIsoDate(endRaw),
    current,
  };
};

export const buildDateString = (startDate: string, endDate: string, current: boolean): string => {
  if (!startDate && !endDate && !current) return "";
  const presentLabel = t`Present`;
  if (current) return startDate ? `${startDate} - ${presentLabel}` : "";
  if (!endDate) return startDate;
  return `${startDate} - ${endDate}`;
};
